import { useState } from 'react';
import type { OrganisationRecord } from '@ni/shared';
import {
  ORGANISATION_INVESTORS_HEAD,
  ORGANISATION_LEADER_TIER_HEADS,
  ORGANISATION_LEADERSHIP_FEWER,
  ORGANISATION_LEADERSHIP_HEAD,
  ORGANISATION_NO_LEADERSHIP,
  showMoreLine,
} from '../../lib/copy';
import { InvestorRow, LeadershipRow } from '../../components/organisations/people-rows';
import {
  investorCards,
  leaderCards,
  leaderGroups,
} from '../../components/organisations/people-groups';
import { useOrganisationRecord } from './record';
import { EmptyLine } from '../../components/ui/empty-line';
import { Section } from '../../components/ui/section';
import './organisation.css';

/**
 * **The organisation's People tab** (owner ruling, 2026-09-10: "The organization
 * Overview tab is now a bit large. Take the leadership and investors sections
 * and move them to a 'People' tab").
 *
 * Both blocks arrive here exactly as the Overview drew them, in the order they
 * were in and under the same heads: who runs it, then who backed it. Nothing
 * about either block changed on the way — the only thing that moved is which tab
 * a reader is standing on when they read it.
 *
 * The tab is the answer to one question, which is who the people around this
 * company are, and it is the reason the split works: the Overview had grown to
 * the offer, the brief, what a source said the place is, and then two lists of
 * names, so a reader who came for the brief scrolled past the names and a reader
 * who came for the names scrolled past the brief. Neither list is a reading the
 * office has to wait for — both come off the same record the Overview reads — so
 * the tab costs nothing but the click.
 *
 * The blocks keep their own rules about being empty, and they are different
 * rules for a reason: every organisation has somebody running it, so a record
 * with no leadership is a gap the office can close and the block says so;
 * most organisations have no stated investors, so that block is absent rather
 * than saying nothing over a family practice.
 */

/**
 * **The tab, given the record** — the split this route makes everywhere: nothing
 * here fetches, so it is asserted as static markup.
 *
 * Leadership is grouped by the band the server ranked each role in, in columns
 * across the page. Staff and the assistants/advisers/former band fold behind one
 * line when anybody ranks above them: a large company names dozens of staff, and
 * the reader came for who runs it.
 */
export function OrganisationPeopleView({ record }: { record: OrganisationRecord }) {
  const { leadership, investors } = record;
  const [unfolded, setUnfolded] = useState(false);
  const groups = leaderGroups(leaderCards(leadership));
  const foldedCount = groups
    .filter((group) => group.folded)
    .reduce((sum, group) => sum + group.cards.length, 0);
  const shown = unfolded ? groups : groups.filter((group) => !group.folded);
  // One band is one list; heads earn their place only when there is a split.
  const headed = groups.length > 1;
  const investorRows = investorCards(investors);

  return (
    <>
      <Section title={ORGANISATION_LEADERSHIP_HEAD}>
        {leadership.length === 0 ? (
          <EmptyLine>{ORGANISATION_NO_LEADERSHIP}</EmptyLine>
        ) : (
          <>
            {shown.map((group) => (
              <div key={group.tier} className="org-people-group">
                {headed ? (
                  <h3 className="sc org-people-head">
                    {ORGANISATION_LEADER_TIER_HEADS[group.tier]}
                    <span className="num"> · {group.cards.length}</span>
                  </h3>
                ) : null}
                <ul className="org-people">
                  {group.cards.map((leader) => (
                    <LeadershipRow key={leader.person_entity_id} leader={leader} />
                  ))}
                </ul>
              </div>
            ))}
            {foldedCount > 0 ? (
              <button type="button" className="linkish" onClick={() => setUnfolded(!unfolded)}>
                {unfolded ? ORGANISATION_LEADERSHIP_FEWER : showMoreLine(foldedCount)}
              </button>
            ) : null}
          </>
        )}
      </Section>

      {/* **Under Leadership, and only where there is one** (owner ruling,
          2026-09-10: "A separate section from leadership"). Who runs a company
          and who backed it are two questions, and the office asks the first of
          every organisation and the second of a few — so this block has no empty
          line, and is absent rather than saying nothing. */}
      {investorRows.length > 0 ? (
        <Section title={ORGANISATION_INVESTORS_HEAD}>
          <ul className="org-people">
            {investorRows.map((card) => (
              <InvestorRow key={card.investor.entity_id} card={card} />
            ))}
          </ul>
        </Section>
      ) : null}
    </>
  );
}

/**
 * The People tab as the route mounts it. It reads the record off the shell's
 * outlet context and nothing else — there is no run to poll here and no door
 * that writes, both of those being about the record and so the shell's or the
 * Overview's.
 */
export function OrganisationPeopleTab() {
  const { record } = useOrganisationRecord();
  return <OrganisationPeopleView record={record} />;
}
